import { apiGet, apiSend } from '@/lib/api'

/**
 * Organisation settings — one document, read and written whole.
 *
 * The service keeps these in a single `org_config` JSONB doc rather than a
 * column per knob, so the client models it the same way: the page edits a draft
 * of the whole thing and saves it in one call. That is also what makes a single
 * save bar honest — there is one write, not eight.
 *
 * Shapes follow `services/frontend/lib/api/settings.ts`, trimmed to what this
 * pane actually offers and camelCased like the rest of `src/data/`.
 *
 * One field from the old settings page is deliberately gone rather than
 * unimplemented: the **share email message**. The copy recipients get is now
 * fixed, so the only thing an organisation chooses about that email is the mark
 * at the top of it.
 */

/* ---------- Recording ---------- */

/**
 * What gets recorded when nobody has said otherwise.
 *
 * `internal` means every attendee is in the tenant — one external address on
 * the invite is enough to make a meeting not internal, which is the whole point
 * of the setting.
 */
export type RecordingPolicy = 'none' | 'internal' | 'all'

/** Mirrors the service's `policies.recording.video`. */
export type VideoMode = 'none' | 'screenshare' | 'all'

export interface RecordingSettings {
  defaultPolicy: RecordingPolicy
  /**
   * Inviting this address to a meeting records it, whatever the default says.
   * Meaningless under `all`, where everything eligible is recorded anyway.
   *
   * Read-only. The address IS the mailbox, provisioned when the tenant is set
   * up — editing the string would rename nothing and only make this page lie.
   * Changing it is a vendor operation.
   */
  readonly recorderInviteEmail: string
  /** Extra addresses that route to the same mailbox. Read-only, as above. */
  readonly recorderInviteAliases: readonly string[]
  /**
   * How much video a recording keeps.
   *
   * NOT a switch for shared screens, which is what this was until the service
   * was asked what it could actually do. The recorder captures one stage, and
   * a share arrives as part of it — so "the speakers but not the slide" would
   * mean dropping the frames the slide appears in, not filtering them.
   *
   * What it can do is keep less: `screenshare` throws away everything except
   * what was on screen, `none` keeps audio alone. `all` is the default because
   * the slide is usually the substance of the meeting and the transcript loses
   * every figure that was only ever shown.
   */
  videoMode: VideoMode
}

/* ---------- Transcription ---------- */

/**
 * Words the transcriber would otherwise get wrong.
 *
 * Speech-to-text is biased toward the language it was trained on, so a company
 * name, a product or a piece of jargon comes out as the nearest ordinary word
 * and stays wrong in the notes, the search index and every answer built on
 * them. A keyterm is a hint, not a rule: it raises the odds of a spelling, it
 * does not insert one.
 *
 * Attendee names are added automatically from the invite and do not belong
 * here. Spaces carry their own lists on top of this one.
 */
export interface TranscriptionSettings {
  keyterms: string[]
}

/** The server's own limits. Mirrored here so the editor rejects what a save would. */
export const KEYTERM_LIMITS = {
  maxTerms: 200,
  maxChars: 49,
  maxWords: 5,
  /** Characters the provider's prompt format cannot carry. */
  forbidden: /[<>{}[\]\\]/,
} as const

/** Why a single term is not acceptable, or null when it is. */
export function keytermError(term: string): string | null {
  if (term.length > KEYTERM_LIMITS.maxChars) return 'longer than 49 characters'
  if (term.split(' ').length > KEYTERM_LIMITS.maxWords) return 'more than 5 words'
  if (KEYTERM_LIMITS.forbidden.test(term)) return 'contains < > { } [ ] or \\'
  return null
}

/**
 * Text area to stored list: one term per line, whitespace collapsed, blanks
 * dropped, case-insensitively deduplicated — the same normalisation the server
 * applies, so what the field reports is what gets saved.
 */
export function parseKeyterms(text: string): string[] {
  const seen = new Set<string>()
  const terms: string[] = []
  for (const line of text.split('\n')) {
    const term = line.replace(/\s+/g, ' ').trim()
    if (!term) continue
    const key = term.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    terms.push(term)
  }
  return terms
}

/**
 * Why the draft cannot be saved, or null when it can.
 *
 * The server rejects the whole document, not the offending field, so a save
 * that goes out invalid loses every other change in the draft with it. This is
 * the same rule the keyterm field renders — stated once, so the field and the
 * save bar cannot disagree about whether the list is acceptable.
 */
export function settingsError(draft: OrgSettings): string | null {
  const { keyterms } = draft.transcription
  if (keyterms.length > KEYTERM_LIMITS.maxTerms) {
    return `Too many keyterms: ${keyterms.length} of ${KEYTERM_LIMITS.maxTerms}.`
  }
  const bad = keyterms.find((term) => keytermError(term) !== null)
  return bad ? `Keyterm “${bad}” is ${keytermError(bad)}.` : null
}

/* ---------- Meeting bot ---------- */

/**
 * How the recorder presents itself in the call.
 *
 * There is no bot name here on purpose. The recorder joins as a real signed-in
 * account from the tenant's pool, so what participants see in the roster is
 * that account's display name in the directory — a field here would be a
 * setting that silently does nothing.
 */
export interface BotSettings {
  /** Off means it joins without saying anything. */
  announce: boolean
  /** Posted to the meeting chat on joining. Only sent while `announce`. */
  message: string
  /** Data URL. The bot's video feed while it is recording. */
  recordingImage: string | null
  /** Data URL. Shown while paused. Falls back to `recordingImage` when unset. */
  pausedImage: string | null
}

/* ---------- Access ---------- */

export type MeetingVisibility = 'private' | 'organization'
export type GovernanceRole = 'org_admin' | 'meeting_viewer'

export interface VisibilitySettings {
  defaultVisibility: MeetingVisibility
  /** Who may flip a meeting between private and organisation-visible. */
  managementRole: GovernanceRole
  /** Whether a viewer may take a visible meeting private. Admins always may. */
  allowViewerPrivateOverrides: boolean
}

export interface DeletionSettings {
  /** Off blocks deletion everywhere, including the API. */
  enabled: boolean
  allowedRole: GovernanceRole
}

/* ---------- Branding ---------- */

/**
 * The two marks the meeting email needs.
 *
 * One logo cannot serve both: the email renders on white in most clients and on
 * near-black in the ones honouring a dark colour scheme, and a mark that is
 * legible on one disappears on the other. Both are optional — the email falls
 * back to the product mark.
 */
export interface EmailBranding {
  /** Data URL. Dark mark, used on the light email background. */
  logoBlack: string | null
  /** Data URL. Light mark, used on the dark email background. */
  logoWhite: string | null
}

/* ---------- Integrations ---------- */

/**
 * Connection state, not a preference — it is changed by a consent round trip
 * with Microsoft rather than by editing a field, which is why this subtree is
 * excluded from the settings draft and its buttons act immediately.
 */
export interface MicrosoftTeamsSettings {
  connected: boolean
  tenantName: string | null
  /** Graph may read meeting transcripts for the tenant. */
  transcriptAccess: boolean
  /** Transcripts carry speaker identities rather than "Speaker 1". */
  speakerAttribution: boolean
}

/**
 * Only what an administrator can change from the app. Model, transcription and
 * Notion credentials are deployment configuration and never reach the client.
 */
export interface IntegrationSettings {
  microsoftTeams: MicrosoftTeamsSettings
}

export interface OrgSettings {
  recording: RecordingSettings
  transcription: TranscriptionSettings
  bot: BotSettings
  visibility: VisibilitySettings
  deletion: DeletionSettings
  email: EmailBranding
  integrations: IntegrationSettings
}

/* ---------- The wire ---------- */

/**
 * Reading the settings takes five requests and writing them takes four.
 *
 * The service keeps this document in four places — the org-config blob behind
 * `/api/settings`, and a table each for recording, visibility and deletion —
 * plus a fifth, `microsoft-teams`, which is connection state rather than
 * preference. The page still edits one draft and saves it once; the fan-out is
 * here so that the single save bar stays honest about being a single decision.
 *
 * **One field on this page still has nowhere to go**: the second email logo.
 * The service stores one mark and the page offers a light and a dark one, so
 * the light one is not saved. It is marked below rather than quietly dropped.
 */

interface WireResolved {
  bot_message: string | null
  transcription_keyterms: string[] | null
  bot_image_url: string | null
  bot_image_not_recording_url: string | null
  logo_email_base64: string | null
}

interface WireRecording {
  default_policy: RecordingPolicy | null
  recorder_invitee_email: string | null
  recorder_invitee_aliases: string[] | null
  video_mode: VideoMode | null
}

function normalizeVideoMode(value: unknown): VideoMode {
  return value === 'none' || value === 'screenshare' || value === 'all' ? value : 'all'
}

interface WireVisibility {
  default_visibility: MeetingVisibility
  allow_private_overrides: boolean
  visibility_management_role: GovernanceRole
}

interface WireDeletion {
  deletion_enabled: boolean
  allowed_role: GovernanceRole
}

interface WireTeams {
  connected: boolean
  microsoft_tenant_name: string | null
  graph_transcript_access_enabled: boolean | null
  speaker_attribution_enabled: boolean | null
}

export async function getOrgSettings(): Promise<OrgSettings> {
  const [resolved, recording, visibility, deletion, teams] = await Promise.all([
    apiGet<WireResolved>('/api/settings'),
    apiGet<WireRecording>('/api/settings/recording'),
    apiGet<WireVisibility>('/api/settings/meeting-visibility'),
    apiGet<WireDeletion>('/api/settings/meeting-deletion'),
    apiGet<WireTeams>('/api/settings/microsoft-teams'),
  ])

  return {
    recording: {
      defaultPolicy: recording.default_policy ?? 'internal',
      recorderInviteEmail: recording.recorder_invitee_email ?? '',
      recorderInviteAliases: recording.recorder_invitee_aliases ?? [],
      videoMode: normalizeVideoMode(recording.video_mode),
    },
    transcription: {
      keyterms: resolved.transcription_keyterms ?? [],
    },
    bot: {
      // The service has no switch for whether the bot speaks; it has a message,
      // and an empty message is a bot that says nothing. Deriving the toggle
      // from the text keeps the pair consistent — a `bot_dispatch_notice_enabled`
      // field does exist, but nothing in any service reads it.
      announce: Boolean(resolved.bot_message?.trim()),
      message: resolved.bot_message ?? '',
      recordingImage: resolved.bot_image_url,
      pausedImage: resolved.bot_image_not_recording_url,
    },
    visibility: {
      defaultVisibility: visibility.default_visibility,
      managementRole: visibility.visibility_management_role,
      allowViewerPrivateOverrides: visibility.allow_private_overrides,
    },
    deletion: {
      enabled: deletion.deletion_enabled,
      allowedRole: deletion.allowed_role,
    },
    email: {
      logoBlack: resolved.logo_email_base64,
      // NOT WIRED. The service stores one email logo; this page offers a light
      // and a dark mark because the email renders on both. Wiring the pair
      // needs a second column, so the dark one is the one that exists.
      logoWhite: null,
    },
    integrations: {
      microsoftTeams: {
        connected: teams.connected,
        tenantName: teams.microsoft_tenant_name,
        transcriptAccess: Boolean(teams.graph_transcript_access_enabled),
        speakerAttribution: Boolean(teams.speaker_attribution_enabled),
      },
    },
  }
}

/**
 * One draft, four writes, and the read that follows is the truth.
 *
 * The writes are concurrent because they touch unrelated tables and the page
 * has already validated the whole document; a sequential chain would only make
 * a partial failure land in a different order, not prevent one.
 */
export async function saveOrgSettings(next: OrgSettings): Promise<OrgSettings> {
  await Promise.all([
    apiSend('PUT', '/api/settings', {
      transcription_keyterms: next.transcription.keyterms,
      // Announce off means the bot joins silently, which the service expresses
      // as having nothing to post.
      bot_message: next.bot.announce ? next.bot.message : '',
      logo_email_base64: next.email.logoBlack,
    }),
    apiSend('PUT', '/api/settings/recording', {
      default_policy: next.recording.defaultPolicy,
      video_mode: next.recording.videoMode,
      // The address is provisioned with the tenant and read-only on this page,
      // but the endpoint replaces what it is not sent — omitting these would
      // clear the mailbox the whole invite path depends on.
      recorder_invitee_email: next.recording.recorderInviteEmail,
      recorder_invitee_aliases: next.recording.recorderInviteAliases,
    }),
    apiSend('PUT', '/api/settings/meeting-visibility', {
      default_visibility: next.visibility.defaultVisibility,
      visibility_management_role: next.visibility.managementRole,
      allow_private_overrides: next.visibility.allowViewerPrivateOverrides,
    }),
    apiSend('PUT', '/api/settings/meeting-deletion', {
      deletion_enabled: next.deletion.enabled,
      allowed_role: next.deletion.allowedRole,
    }),
  ])

  // Re-read rather than echo the draft: the server normalises (a policy it does
  // not recognise, an empty alias list) and the page should show what was
  // actually stored.
  return getOrgSettings()
}

/* ---------- Actions ---------- */

/**
 * Tenant-wide Microsoft Graph consent.
 *
 * Consent happens at Microsoft, so this hands the browser off and never returns
 * a settings document — the caller's `onSuccess` invalidation is what refreshes
 * the pane when the tab comes back.
 */
export async function connectMicrosoftTenant(): Promise<OrgSettings> {
  const { authorization_url } = await apiSend<{ authorization_url: string }>(
    'POST',
    '/api/settings/microsoft-teams/admin-consent'
  )
  window.location.href = authorization_url
  return getOrgSettings()
}

/**
 * NOT WIRED — there is no endpoint to revoke consent.
 *
 * Withdrawing a tenant-wide Graph grant is done in Microsoft's own admin centre
 * (Enterprise applications → the app → Permissions), and the service has no
 * route that would do it. Failing loudly is the honest behaviour: a button that
 * appeared to work and left the grant in place would be worse than one that
 * says where the switch actually is.
 */
export function disconnectMicrosoftTenant(): Promise<OrgSettings> {
  return Promise.reject(
    new Error(
      'Revoke access in the Microsoft 365 admin centre, under Enterprise applications.'
    )
  )
}

/** Both switches are one tenant-wide step in the Teams admin centre. */
export async function completeTeamsTranscriptSetup(): Promise<OrgSettings> {
  await apiSend('POST', '/api/settings/microsoft-teams/manual-setup')
  return getOrgSettings()
}

/* ---------- Labels ---------- */

export const RECORDING_POLICIES: { value: RecordingPolicy; label: string; description: string }[] = [
  {
    value: 'none',
    label: 'Record nothing',
    description: 'Only meetings someone opts in, one at a time.',
  },
  {
    value: 'internal',
    label: 'Record internal meetings',
    description: 'Every attendee is in the organisation.',
  },
  {
    value: 'all',
    label: 'Record everything eligible',
    description: 'Including meetings with external attendees.',
  },
]

export const VIDEO_MODES: { value: VideoMode; label: string; description: string }[] = [
  {
    value: 'all',
    label: 'Speakers and shared screens',
    description:
      'The whole stage, as the room saw it. Anything a participant puts on screen is in the recording, including whatever else was on that window or display.',
  },
  {
    value: 'screenshare',
    label: 'Shared screens only',
    description:
      'Only what was on screen is kept. Faces are not recorded, and a meeting where nobody shared has no video at all.',
  },
  {
    value: 'none',
    label: 'No video',
    description:
      'Audio and the transcript alone. Anything that was shown rather than said is not on the record.',
  },
]

export const VISIBILITIES: { value: MeetingVisibility; label: string; description: string }[] = [
  {
    value: 'private',
    label: 'Private to participants',
    description: 'Only people on the invite, plus anyone given access.',
  },
  {
    value: 'organization',
    label: 'Visible to the organisation',
    description: 'Anyone in the tenant can find and read it.',
  },
]

export const GOVERNANCE_ROLES: { value: GovernanceRole; label: string }[] = [
  { value: 'org_admin', label: 'Organisation admins only' },
  { value: 'meeting_viewer', label: 'Everyone who can view the meeting' },
]
